/**
 * Settings → Tasks: types, statuses, module defaults (admin).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { TaskModuleSettingsOut, TaskStatusAdmin, TaskTypeAdmin } from '../types/task';

interface TaskModuleSettingsTabProps {
  onError?: (msg: string) => void;
}

const TaskModuleSettingsTabV2: React.FC<TaskModuleSettingsTabProps> = ({ onError }) => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState<TaskTypeAdmin[]>([]);
  const [statuses, setStatuses] = useState<TaskStatusAdmin[]>([]);
  const [settings, setSettings] = useState<TaskModuleSettingsOut | null>(null);

  const [typeDialog, setTypeDialog] = useState(false);
  const [typeEdit, setTypeEdit] = useState<TaskTypeAdmin | null>(null);
  const [typeForm, setTypeForm] = useState({ code: '', name: '', sort_order: 0, is_active: true });

  const [statusDialog, setStatusDialog] = useState(false);
  const [statusEdit, setStatusEdit] = useState<TaskStatusAdmin | null>(null);
  const [statusForm, setStatusForm] = useState({
    code: '',
    name: '',
    sort_order: 0,
    is_active: true,
    is_closed: false,
    is_positive_completion: false,
  });

  const [defaultTypeId, setDefaultTypeId] = useState<string>('');
  const [reminderCodes, setReminderCodes] = useState('1d, 1h');
  const [savingDefaults, setSavingDefaults] = useState(false);

  const load = useCallback(async () => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [ty, st, se] = await Promise.all([
        apiService.getTaskConfigTypes(),
        apiService.getTaskConfigStatuses(),
        apiService.getTaskConfigSettings(),
      ]);
      setTypes(ty);
      setStatuses(st);
      setSettings(se);
      setDefaultTypeId(se.default_task_type_id || '');
      setReminderCodes((se.reminder_lead_codes || []).join(', '));
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Failed to load task configuration';
      onError?.(String(msg));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, onError]);

  useEffect(() => {
    void load();
  }, [load]);

  const openNewType = () => {
    setTypeEdit(null);
    setTypeForm({ code: '', name: '', sort_order: 0, is_active: true });
    setTypeDialog(true);
  };

  const openEditType = (t: TaskTypeAdmin) => {
    setTypeEdit(t);
    setTypeForm({ code: t.code, name: t.name, sort_order: t.sort_order, is_active: t.is_active });
    setTypeDialog(true);
  };

  const saveType = async () => {
    try {
      if (typeEdit) {
        await apiService.patchTaskConfigType(typeEdit.id, {
          name: typeForm.name,
          sort_order: typeForm.sort_order,
          is_active: typeForm.is_active,
          code: typeForm.code !== typeEdit.code ? typeForm.code : undefined,
        });
      } else {
        await apiService.createTaskConfigType({
          code: typeForm.code,
          name: typeForm.name,
          sort_order: typeForm.sort_order,
        });
      }
      setTypeDialog(false);
      await load();
    } catch (e: any) {
      onError?.(e?.response?.data?.detail || 'Failed to save task type');
    }
  };

  const openNewStatus = () => {
    setStatusEdit(null);
    setStatusForm({
      code: '',
      name: '',
      sort_order: 0,
      is_active: true,
      is_closed: false,
      is_positive_completion: false,
    });
    setStatusDialog(true);
  };

  const openEditStatus = (s: TaskStatusAdmin) => {
    setStatusEdit(s);
    setStatusForm({
      code: s.code,
      name: s.name,
      sort_order: s.sort_order,
      is_active: s.is_active,
      is_closed: s.is_closed,
      is_positive_completion: s.is_positive_completion,
    });
    setStatusDialog(true);
  };

  const saveStatus = async () => {
    try {
      if (statusEdit) {
        await apiService.patchTaskConfigStatus(statusEdit.id, {
          name: statusForm.name,
          sort_order: statusForm.sort_order,
          is_active: statusForm.is_active,
          is_closed: statusForm.is_closed,
          is_positive_completion: statusForm.is_positive_completion,
          code: statusForm.code !== statusEdit.code ? statusForm.code : undefined,
        });
      } else {
        await apiService.createTaskConfigStatus({
          code: statusForm.code,
          name: statusForm.name,
          sort_order: statusForm.sort_order,
          is_closed: statusForm.is_closed,
          is_positive_completion: statusForm.is_positive_completion,
        });
      }
      setStatusDialog(false);
      await load();
    } catch (e: any) {
      onError?.(e?.response?.data?.detail || 'Failed to save task status');
    }
  };

  const saveDefaults = async () => {
    try {
      setSavingDefaults(true);
      const codes = reminderCodes
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const body = {
        default_task_type_id: defaultTypeId || null,
        reminder_lead_codes: codes.length ? codes : ['1d', '1h'],
      };
      const se = await apiService.patchTaskConfigSettings(body);
      setSettings(se);
    } catch (e: any) {
      onError?.(e?.response?.data?.detail || 'Failed to save task module settings');
    } finally {
      setSavingDefaults(false);
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          Tasks
        </Typography>
        <Alert severity="warning" variant="outlined">
          Task types, statuses, and defaults can only be changed by an administrator.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        Tasks module
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure task types and statuses used across the app. Terminal statuses are marked closed; completed-style outcomes
        can be flagged for reporting.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Types" />
        <Tab label="Statuses" />
        <Tab label="Defaults" />
      </Tabs>

      {tab === 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography fontWeight={600}>Task types</Typography>
            <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={openNewType}>
              Add type
            </Button>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="right">Order</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {types.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{t.code}</TableCell>
                  <TableCell>{t.name}</TableCell>
                  <TableCell align="right">{t.sort_order}</TableCell>
                  <TableCell>{t.is_active ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openEditType(t)}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 1 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography fontWeight={600}>Task statuses</Typography>
            <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={openNewStatus}>
              Add status
            </Button>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="right">Order</TableCell>
                <TableCell>Terminal</TableCell>
                <TableCell>Positive completion</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statuses.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.code}</TableCell>
                  <TableCell>{s.name}</TableCell>
                  <TableCell align="right">{s.sort_order}</TableCell>
                  <TableCell>{s.is_closed ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{s.is_positive_completion ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{s.is_active ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openEditStatus(s)}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 2 && (
        <Paper variant="outlined" sx={{ p: 2, maxWidth: 520 }}>
          <Typography fontWeight={600} sx={{ mb: 2 }}>
            Module defaults
          </Typography>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="def-task-type">Default task type</InputLabel>
              <Select
                labelId="def-task-type"
                label="Default task type"
                value={defaultTypeId}
                onChange={(e) => setDefaultTypeId(String(e.target.value))}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {types.filter((t) => t.is_active).map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name} ({t.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Reminder lead codes"
              size="small"
              fullWidth
              value={reminderCodes}
              onChange={(e) => setReminderCodes(e.target.value)}
              helperText="Comma or space separated (e.g. 1d, 1h). Used for reminder scheduling."
            />
            <Button variant="contained" onClick={() => void saveDefaults()} disabled={savingDefaults}>
              Save defaults
            </Button>
            {settings && (
              <Typography variant="caption" color="text.secondary">
                Last configured default type: {settings.default_task_type_id || 'none'}
              </Typography>
            )}
          </Stack>
        </Paper>
      )}

      <Dialog open={typeDialog} onClose={() => setTypeDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{typeEdit ? 'Edit task type' : 'New task type'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Code"
              value={typeForm.code}
              onChange={(e) => setTypeForm((f) => ({ ...f, code: e.target.value }))}
              disabled={!!typeEdit}
              helperText="Unique identifier (cannot change after create)."
              fullWidth
              size="small"
            />
            <TextField
              label="Name"
              value={typeForm.name}
              onChange={(e) => setTypeForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Sort order"
              type="number"
              value={typeForm.sort_order}
              onChange={(e) => setTypeForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
              fullWidth
              size="small"
            />
            {typeEdit && (
              <FormControlLabel
                control={
                  <Switch checked={typeForm.is_active} onChange={(e) => setTypeForm((f) => ({ ...f, is_active: e.target.checked }))} />
                }
                label="Active"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTypeDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void saveType()} disabled={!typeForm.code.trim() || !typeForm.name.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{statusEdit ? 'Edit task status' : 'New task status'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Code"
              value={statusForm.code}
              onChange={(e) => setStatusForm((f) => ({ ...f, code: e.target.value }))}
              disabled={!!statusEdit}
              helperText="Unique identifier (cannot change after create)."
              fullWidth
              size="small"
            />
            <TextField
              label="Name"
              value={statusForm.name}
              onChange={(e) => setStatusForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Sort order"
              type="number"
              value={statusForm.sort_order}
              onChange={(e) => setStatusForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
              fullWidth
              size="small"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusForm.is_closed}
                  onChange={(e) => setStatusForm((f) => ({ ...f, is_closed: e.target.checked }))}
                />
              }
              label="Terminal / closed status"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusForm.is_positive_completion}
                  onChange={(e) => setStatusForm((f) => ({ ...f, is_positive_completion: e.target.checked }))}
                />
              }
              label="Positive completion (e.g. completed successfully)"
            />
            {statusEdit && (
              <FormControlLabel
                control={
                  <Switch
                    checked={statusForm.is_active}
                    onChange={(e) => setStatusForm((f) => ({ ...f, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void saveStatus()} disabled={!statusForm.code.trim() || !statusForm.name.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskModuleSettingsTabV2;
